import { useState, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { getUserProfile, updateUserProfile, updateUserProfileImage, changePassword } from '../../store/slices/authSlice';

/**
 * Custom hook for Airliner profile management using Redux
 * @returns {Object} Profile state and methods
 */
const useAirlinerProfile = () => {
  const dispatch = useAppDispatch();
  const { user, profileLoading, passwordChangeLoading } = useAppSelector((state) => state.auth);
  
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [error, setError] = useState(null);

  const loadProfile = useCallback(async () => {
    try {
      setError(null);
      await dispatch(getUserProfile()).unwrap();
    } catch (err) {
      setError(err);
    }
  }, [dispatch]);

  const updateProfile = useCallback(async (profileData) => {
    try {
      setError(null);
      const updatedUser = await dispatch(updateUserProfile(profileData)).unwrap();
      return updatedUser;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const uploadLogo = useCallback(async (logoFile) => {
    try {
      setError(null);
      
      // Convert file to base64 and upload
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64Image = e.target.result;
            const result = await dispatch(updateUserProfileImage(base64Image)).unwrap();
            resolve({ logoUrl: result.image_url });
          } catch (err) {
            reject(err);
          }
        };
        
        reader.onerror = (error) => {
          reject(error);
        };
        
        reader.readAsDataURL(logoFile);
      });
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const uploadAOCFile = useCallback(async (aocFile) => {
    try {
      setError(null);
      
      // Convert file to base64 and upload
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64File = e.target.result;
            
            // Call the AOC upload API
            const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
            const response = await fetch(`${apiUrl}/auth/profile/aoc`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${sessionStorage.getItem('token')}`
              },
              body: JSON.stringify({
                aoc_file_data: base64File
              })
            });

            const data = await response.json();

            if (!response.ok) {
              throw new Error(data.message || 'Failed to upload AOC file');
            }
            
            // Reload profile to get updated AOC file
            await dispatch(getUserProfile()).unwrap();
            
            resolve({ aocUrl: data.data.aoc_url, aoc: data.data.user.aoc });
          } catch (err) {
            reject(err);
          }
        };
        
        reader.onerror = (error) => {
          reject(error);
        };
        
        reader.readAsDataURL(aocFile);
      });
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const changeUserPassword = useCallback(async (passwordData) => {
    try {
      setError(null);
      await dispatch(changePassword({
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword
      })).unwrap();
      
      return { success: true, message: 'Password changed successfully' };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const enableTwoFactor = useCallback(async (twoFactorData) => {
    try {
      setError(null);
      
      // Call the 2FA enable API
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
      const response = await fetch(`${apiUrl}/2fa/enable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        },
        body: JSON.stringify({
          method: twoFactorData.method || 'email',
          security_questions: twoFactorData.security_questions || []
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to enable 2FA');
      }
      
      setTwoFactorEnabled(true);
      
      // Reload profile to get updated 2FA status
      await dispatch(getUserProfile()).unwrap();
      
      return { 
        success: true,
        method: data.data.method,
        enabled: data.data.enabled
      };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const disableTwoFactor = useCallback(async () => {
    try {
      setError(null);
      
      // Call the 2FA disable API
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
      const response = await fetch(`${apiUrl}/2fa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to disable 2FA');
      }
      
      setTwoFactorEnabled(false);
      
      // Reload profile to get updated 2FA status
      await dispatch(getUserProfile()).unwrap();
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [dispatch]);

  const verifyTwoFactor = useCallback(async (code) => {
    try {
      setError(null);
      
      // Call the 2FA verify API
      const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://jetscanner360.com/api/v1';
      const response = await fetch(`${apiUrl}/2fa/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: user?.email,
          otp: code
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Invalid verification code');
      }
      
      return { success: true, message: data.message };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [user]);

  return {
    profile: user,
    twoFactorEnabled: user?.twofa || user?.Twofa || twoFactorEnabled,
    loading: profileLoading,
    passwordLoading: passwordChangeLoading,
    error,
    loadProfile,
    updateProfile,
    uploadLogo,
    uploadAOCFile,
    changePassword: changeUserPassword,
    enableTwoFactor,
    disableTwoFactor,
    verifyTwoFactor
  };
};

export default useAirlinerProfile;